import { useImperativeHandle, forwardRef } from 'react';
import { DropTarget } from 'react-dnd';
import FormElements from '../form-elements';
import ItemTypes from '../ItemTypes';

function is_container(item) {
	if (item.itemType !== ItemTypes.CARD) {
		const { data } = item;

		if (data?.is_container) {
			return true;
		} else if (data?.field_name) {
			return data.field_name.indexOf('_col_row') > -1;
		}
	}

	return false;
}

// Protection marker constants
const PROTECTION_MARKERS = {
	PROJECT_TITLE: 'project-title',
	CORE_APPLICATION: 'core-application'
};

/**
 * Checks if a field has protection markers in its custom_options
 * Protected fields include 'project-title' and 'core-application' (both are nested fields)
 * @param {Object} fieldData - Field data object to check
 * @returns {boolean} True if field is protected, false otherwise
 */
const isFieldProtected = (fieldData) => {
	if (!fieldData?.custom_options || !Array.isArray(fieldData.custom_options)) {
		return false;
	}

	return fieldData.custom_options.some(
		(option) =>
			option === PROTECTION_MARKERS.PROJECT_TITLE || option === PROTECTION_MARKERS.CORE_APPLICATION
	);
};

/**
 * Checks if the target element or any sibling in the same container is protected
 * This is optimized to only check the current drop target and its siblings (via items array)
 * instead of searching through the entire allData array
 * @param {Object} targetElement - The element being dropped on
 * @param {Array} siblingIds - Array of IDs for all siblings in the container (props.items)
 * @param {Function} getDataById - Function to get element data by ID
 * @returns {boolean} True if target or any sibling is protected
 */
const hasProtectedSiblings = (targetElement, siblingIds, getDataById) => {
	if (!targetElement || !siblingIds?.length || !getDataById) {
		return false;
	}

	// Check if the target element itself is protected
	if (isFieldProtected(targetElement)) {
		return true;
	}

	// Check if any sibling in the same container is protected
	// This only iterates through siblings in the same container, not the entire form
	return siblingIds.some((siblingId) => {
		if (!siblingId) return false;
		const sibling = getDataById(siblingId);
		return sibling && isFieldProtected(sibling);
	});
};

const Dustbin = forwardRef(
	(
		{
			draggedItem,
			parent_index,
			canDrop,
			isOver,
			connectDropTarget,
			items,
			col,
			getDataById,
			...rest
		},
		ref
	) => {
		const item = getDataById(items[col]);
		useImperativeHandle(
			ref,
			() => ({
				onDrop: () => {}
			}),
			[]
		);

		const getStyle = (backgroundColor) => ({
			border: '1px solid rgba(0,0,0,0.2)',
			minHeight: '2rem',
			minWidth: '7rem',
			width: '100%',
			backgroundColor,
			padding: 0,
			float: 'left'
		});

		const getElement = (item, props) => {
			if (!item) return null;

			let Element;

			if (item.custom) {
				switch (item.key) {
					case 'MUI_Checkbox':
						Element = FormElements['Checkboxes'];
						break;
					case 'MUI_RadioButtons':
						Element = FormElements['RadioButtons'];
						break;
					case 'MUI_StartEndDate':
						Element = FormElements['DatePicker'];
						break;
					default:
						Element = FormElements['TextInput'];
						break;
				}
			} else {
				Element = FormElements[item.element || item.key];
			}

			return <Element {...props} key={`form_${item.id}`} data={item} />;
		};

		const element = getElement(item, rest);
		const sameCard = draggedItem ? draggedItem.index === parent_index : false;

		const validNewDropTarget = !sameCard && isOver && canDrop && !draggedItem.data.is_container;
		const backgroundColor = validNewDropTarget ? '#F7F589' : 'rgba(0, 0, 0, .03)';

		return connectDropTarget(<div style={getStyle(backgroundColor)}>{element}</div>);
	}
);

export default DropTarget(
	(props) => props.accepts,
	{
		/**
		 * Determines if a drop operation is allowed on this target
		 * Only performs cheap checks here since this is called on every mouse move during drag
		 * Expensive protection checks are done in drop() which is only called once on actual drop
		 */
		canDrop(props, monitor) {
			const item = monitor.getItem();

			// Block reordering items within same column
			if (props.col === item.col && props.items[props.col] === item.id) {
				return false;
			}

			return true;
		},

		/**
		 * Handles the drop operation
		 * Performs protection validation here (only once on actual drop, not on every mouse move)
		 */
		drop(props, monitor, component) {
			if (!component) {
				return;
			}

			const item = monitor.getItem();

			// Validate required props for protection check
			if (!props.items?.length || !props.getDataById) {
				console.warn('[DropTarget] Drop blocked: Required props unavailable');
				return;
			}

			// Get the target element being dropped on
			const targetElement = props.getDataById(props.items[props.col]);

			// Block drops for a core form if target or any sibling in this container is protected
			if (props.isCoreForm && hasProtectedSiblings(targetElement, props.items, props.getDataById)) {
				const containerLabel = props.data?.label || props.data?.element || 'Container';
				console.log(
					`🚫 DROP BLOCKED: "${containerLabel}" contains protected fields (${Object.values(
						PROTECTION_MARKERS
					).join(', ')})`
				);
				return;
			}

			// Execute drop for non-container items
			if (!is_container(item)) {
				component.onDrop(item);
				if (item.data && typeof props.setAsChild === 'function') {
					const isNew = !item.data.id && !item.data.field_id;
					const data = isNew ? item.onCreate(item.data) : item.data;

					props.setAsChild(props.data, data, props.col);
				}
			}
		}
	},
	(connect, monitor) => ({
		connectDropTarget: connect.dropTarget(),
		draggedItem: monitor.getItem() ? monitor.getItem() : null,
		isOver: monitor.isOver(),
		isOverCurrent: monitor.isOver({ shallow: true }),
		canDrop: monitor.canDrop()
	})
)(Dustbin);
